import { useState } from "react";
import { Box, ChevronDown, Loader2, Pencil, PencilRuler, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useGetAllPackagePresets } from "@/api/packagePreset.api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PackagePresetDto } from "@/types/api/api-types";
import { PackagePresetFormModal } from "./packagePresetFormModal";

type PresetModalState = { preset: PackagePresetDto | null } | null;

export const FedexApiPackageAddMenu = ({
  onAddCustom,
  onAddPreset,
}: {
  onAddCustom: () => void;
  onAddPreset: (preset: PackagePresetDto) => void;
}) => {
  const { t } = useTranslation("boExpeditionForm");
  const { data: presets, isLoading } = useGetAllPackagePresets();
  const [presetModal, setPresetModal] = useState<PresetModalState>(null);

  return (
    <>
      {/* Non-modal so the Dialog opened from an item doesn't inherit the menu's pointer-events lock */}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" type="button" className="self-start!">
            <Plus className="h-4 w-4" />
            {t("fedexPackage.add")}
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-96 w-80">
          <DropdownMenuItem onSelect={onAddCustom} className="bg-amber-50 focus:bg-amber-100">
            <PencilRuler className="size-6!" />
            <div className="flex flex-col">
              <span className="font-semibold">{t("fedexPackageAddMenu.customTitle")}</span>
              <span className="text-xs text-gray-500 italic">
                {t("fedexPackageAddMenu.customDescription")}
              </span>
            </div>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-xs text-gray-500">
            {t("fedexPackageAddMenu.presetsTitle")}
          </DropdownMenuLabel>
          {isLoading && (
            <div className="flex justify-center p-2">
              <Loader2 className="size-4 animate-spin" />
            </div>
          )}
          {!isLoading && !presets?.length && (
            <p className="px-2 py-1.5 text-xs text-gray-500 italic">
              {t("fedexPackageAddMenu.presetsEmpty")}
            </p>
          )}
          {presets?.map((preset) => (
            <div key={preset.id} className="flex flex-row items-center">
              <DropdownMenuItem onSelect={() => onAddPreset(preset)} className="flex-1">
                <Box className="size-6!" />
                <div className="flex flex-col">
                  <span className="font-semibold">{preset.name}</span>
                  <span className="text-xs text-gray-500 italic">
                    {t("fedexPackageAddMenu.presetDetails", {
                      quantity: preset.count,
                      weight: preset.weight,
                      length: preset.length,
                      width: preset.width,
                      height: preset.height,
                    })}
                  </span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => setPresetModal({ preset })}
                title={t("fedexPackageAddMenu.editPreset")}
                aria-label={t("fedexPackageAddMenu.editPreset")}
                className="self-stretch"
              >
                <Pencil className="text-blue-700" />
              </DropdownMenuItem>
            </div>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={() => setPresetModal({ preset: null })}
            className="text-blue-700"
          >
            <Plus className="text-blue-700" />
            {t("fedexPackageAddMenu.newPreset")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <PackagePresetFormModal
        open={!!presetModal}
        onClose={() => setPresetModal(null)}
        preset={presetModal?.preset ?? null}
      />
    </>
  );
};
